import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Linkedin, Twitter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSeo } from '../hooks/useSeo';
import { caureInterview, caureHeroPhoto, caureGallery, caureInterviewUrl } from '../data/caureInterview';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SPEAKER_TAG_AT = [0, 4, 9];

const GalleryFigure = ({ photo, language, index }) => {
  const caption = photo.caption[language] || photo.caption.fr;
  const portrait = photo.orientation === 'portrait';
  return (
    <figure data-testid={`caure-photo-${index + 1}`} className="mt-14 md:mt-20">
      <div className={portrait ? 'max-w-md mx-auto' : ''}>
        <div className="relative overflow-hidden rounded-sm border border-[#3f4816]/60 bg-[#0f1110]">
          <img
            src={photo.src}
            alt={caption}
            loading="lazy"
            className={`w-full h-auto object-cover ${portrait ? 'aspect-[2/3]' : 'aspect-[16/10] md:aspect-[3/2]'}`}
          />
        </div>
      </div>
      <figcaption className="mt-4 flex items-start gap-3 text-[#888680] text-sm md:text-[0.95rem] italic leading-relaxed max-w-2xl mx-auto justify-center">
        <span className="mt-2 inline-block h-px w-6 bg-[#d9fb06] flex-shrink-0" />
        <span>{caption}</span>
      </figcaption>
    </figure>
  );
};

const CaureInterviewPage = () => {
  const { language } = useLanguage();
  const c = caureInterview[language] || caureInterview.fr;
  const heroAlt = caureHeroPhoto.alt[language] || caureHeroPhoto.alt.fr;

  useSeo({
    title: c.seo.title,
    description: c.seo.description,
    url: caureInterviewUrl,
    image: caureHeroPhoto.src,
    imageAlt: heroAlt,
    language,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-white" data-testid="caure-interview-page">
      <Header />

      <main className="pb-24">
        {/* Hero — split layout: text left, portrait right (no overlap on the face) */}
        <section className="relative pt-28 md:pt-32" data-testid="caure-hero">
          <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
            <Link
              to="/"
              data-testid="caure-back-link"
              className="group inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm uppercase tracking-wider mb-8 lg:mb-10"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              {c.ctaBack}
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-px w-10 bg-[#d9fb06]" />
                  <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">
                    {c.hero.label}
                  </span>
                </div>

                <h1
                  data-testid="caure-hero-title"
                  className="font-black text-white text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] mb-6"
                >
                  {c.hero.title}
                </h1>

                <div className="border-l-2 border-[#d9fb06] pl-5 space-y-1 mb-5">
                  {c.hero.roles.map((role, idx) => (
                    <p key={idx} className="text-[#c9c7c0] text-base md:text-lg leading-snug">
                      {role}
                    </p>
                  ))}
                </div>

                <p className="text-[#888680] text-xs md:text-sm uppercase tracking-[0.25em]" data-testid="caure-hero-dateline">
                  {c.hero.dateline}
                </p>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative">
                  <div className="absolute -inset-2 border border-[#d9fb06]/20 rounded-sm" />
                  <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#d9fb06]/10 rounded-sm hidden md:block" />
                  <div className="relative overflow-hidden rounded-sm aspect-[3/2] bg-[#0f1110]">
                    <img
                      src={caureHeroPhoto.src}
                      alt={heroAlt}
                      className="w-full h-full object-cover"
                      data-testid="caure-hero-photo"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 mt-16 md:mt-24" data-testid="caure-intro">
          <div className="space-y-5">
            {c.intro.map((paragraph, idx) => (
              <p
                key={idx}
                className={`leading-relaxed ${
                  idx === 0
                    ? 'text-white text-lg md:text-2xl font-medium'
                    : 'text-[#c9c7c0] text-base md:text-lg'
                }`}
              >
                {paragraph}
              </p>
            ))}
          </div>
          <div className="mt-12 md:mt-16 flex items-center gap-4">
            <div className="h-px flex-1 bg-[#3f4816]" />
            <span className="text-[#888680] text-xs uppercase tracking-[0.25em]">
              {c.interviewer} × {c.speaker}
            </span>
            <div className="h-px flex-1 bg-[#3f4816]" />
          </div>
        </section>

        {/* Q&A */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 mt-16 md:mt-20">
          <div className="space-y-20 md:space-y-28">
            {c.questions.map((item, idx) => {
              const number = String(idx + 1).padStart(2, '0');
              const photo = caureGallery.find((g) => g.afterQuestion === idx);
              return (
                <article key={idx} data-testid={`caure-question-${idx + 1}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
                    <div className="md:col-span-2">
                      <span className="block font-black text-[#d9fb06] text-5xl md:text-6xl leading-none tabular-nums">
                        {number}
                      </span>
                    </div>
                    <div className="md:col-span-10">
                      <h2 className="text-white font-bold text-xl md:text-2xl lg:text-[1.75rem] leading-snug mb-7 md:mb-8">
                        {item.q}
                      </h2>
                      <div className="space-y-5">
                        {item.a.map((paragraph, pIdx) => (
                          <p key={pIdx} className="text-[#c9c7c0] text-base md:text-lg leading-[1.8]">
                            {pIdx === 0 && SPEAKER_TAG_AT.includes(idx) && (
                              <span className="text-[#d9fb06] font-semibold not-italic mr-2">
                                {c.speaker} —
                              </span>
                            )}
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>

                  {photo && <GalleryFigure photo={photo} language={language} index={caureGallery.indexOf(photo)} />}
                </article>
              );
            })}
          </div>

          {/* Separator */}
          <div className="mt-24 md:mt-32 flex items-center justify-center gap-4">
            <div className="h-px w-16 md:w-32 bg-[#3f4816]" />
            <div className="w-2 h-2 rotate-45 bg-[#d9fb06]" />
            <div className="h-px w-16 md:w-32 bg-[#3f4816]" />
          </div>

          {/* Closing CTA */}
          <div
            data-testid="caure-closing-cta"
            className="mt-16 md:mt-20 border border-[#3f4816]/70 bg-[#302f2c]/60 rounded-sm p-8 md:p-12 text-center"
          >
            <h3 className="font-black text-white text-2xl md:text-3xl leading-tight mb-4">
              {c.closing.title}
            </h3>
            <p className="text-[#c9c7c0] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              {c.closing.text}
            </p>
            <Link
              to="/#contact"
              data-testid="caure-contact-cta"
              className="group inline-flex items-center gap-3 bg-[#d9fb06] text-[#1a1c1b] px-7 py-4 rounded-full font-bold text-sm uppercase tracking-wider hover:bg-white transition-all duration-300"
            >
              {c.closing.cta}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Share */}
          <div className="mt-16 flex flex-col items-center gap-5" data-testid="caure-share-block">
            <span className="text-[#888680] text-xs uppercase tracking-[0.25em]">{c.share.heading}</span>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(caureInterviewUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="caure-share-linkedin"
                className="inline-flex items-center gap-2.5 bg-[#0a66c2] text-white px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#0a66c2]/85 hover:scale-[1.02] transition-all duration-300"
              >
                <Linkedin size={18} fill="currentColor" strokeWidth={0} />
                {c.share.linkedin}
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(c.share.shareText)}&url=${encodeURIComponent(caureInterviewUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="caure-share-twitter"
                className="inline-flex items-center gap-2.5 bg-white text-black px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#d9fb06] hover:scale-[1.02] transition-all duration-300"
              >
                <Twitter size={18} fill="currentColor" strokeWidth={0} />
                {c.share.twitter}
              </a>
            </div>
          </div>

          <div className="mt-14 flex justify-center">
            <Link
              to="/"
              data-testid="caure-back-bottom"
              className="group inline-flex items-center gap-3 border border-[#d9fb06] text-[#d9fb06] px-7 py-4 rounded-full font-bold text-sm uppercase tracking-wider hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-all duration-300"
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              {c.ctaBack}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CaureInterviewPage;
